import React, { useState, useRef, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

const TextInput = ({ 
  sendTextToAPI, 
  setTextContent, 
  selectedSpeaker, 
  setSelectedSpeaker, 
  translationEnabled, 
  setTranslationEnabled,  // ✅ Accept prop
  selectedLanguage, 
  setSelectedLanguage,    // ✅ Accept prop
  audioRef,
  textContent 
}) => {  
  const [inputText, setInputText] = useState("");
  const translatedTextRef = useRef(""); // ✅ Use a ref instead of state
  const lastRawInputRef = useRef(""); // ✅ Stores the last unprocessed (original) input

  //Fetch new translation when new language is selected
  useEffect(() => {
    if (translationEnabled && selectedLanguage && inputText.trim() !== "") {
        console.log("🌍 Language changed, fetching new translation...");
        translateText(inputText.trim()).then((translated) => {
            translatedTextRef.current = translated;
            setTextContent(translated);
            sendTextToAPI(translated, selectedSpeaker, true, false); // ✅ Automatically request new audio
        });
    }
}, [selectedLanguage]);

  const handleSubmit = async () => {
    if (inputText.trim() === "") {
        alert("Please enter text before submitting.");
        return;
    }

    let finalText = inputText.trim();

    // ✅ Only reset translation cache if the input before translation has changed
    if (finalText !== lastRawInputRef.current) {  
        console.log("🧹 Resetting translation cache...");
        translatedTextRef.current = ""; // ✅ Clear cache only if user typed something new
    }

    // ✅ Only translate if translation is enabled & the text hasn't already been translated
    if (translationEnabled && selectedLanguage) {
        if (translatedTextRef.current && translatedTextRef.current === textContent) {  
            console.log("✅ Using stored translation...");
            finalText = translatedTextRef.current;
        } else {
            finalText = await translateText(finalText);
            translatedTextRef.current = finalText;
        }
    }

    // ✅ Update last raw input AFTER translation check
    lastRawInputRef.current = inputText.trim();

    // ✅ Avoid unnecessary API calls if the text hasn't changed
    if (finalText !== textContent.trim()) {
        setTextContent(finalText);
        sendTextToAPI(finalText, selectedSpeaker, true, false);
    } else {
        console.log("📌 Text is unchanged. Playing existing audio...");
        if (audioRef.current && audioRef.current.audio) {
            audioRef.current.audio.currentTime = 0;
            audioRef.current.audio.play()
                .then(() => console.log("▶️ Playing existing audio"))
                .catch(error => console.error("Playback error:", error));
        }
    }
};

  
  //Translate them words
  const translateText = async (text) => {
    console.log("🌍 Fetching translation...");

    try {
        const response = await fetch(`${API_BASE_URL}/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text, targetLanguage: selectedLanguage }),
        });

        const data = await response.json();
        if (data.translatedText) {
            console.log("✅ Translated text:", data.translatedText);
            translatedTextRef.current = data.translatedText; // ✅ Store translation using ref
            return data.translatedText;
        } else {
            console.error("Translation failed, using original text.");
            return text;
        }
    } catch (error) {
        console.error("Error translating text:", error);
        return text;
    }
};

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) { // Submit only if Shift is NOT held
            event.preventDefault(); // Prevents newline inside textarea
            handleSubmit();
        }
    };

    return (
      <div className="text-input-container">
          <textarea
            value={inputText}
            onChange={(e) => {
                setInputText(e.target.value);
                if (e.target.value.trim() !== textContent.trim()) {
                    translatedTextRef.current = ""; // ✅ Reset cache only if input truly changes
                }
            }}
            onKeyDown={handleKeyDown}
            rows="4"
            placeholder="Enter text here..."
          ></textarea>
          
          {/* ✅ Translation Toggle */}
          <div className="translation-container">
              <label className="toggle-translation">
                  <input
                      type="checkbox"
                      checked={translationEnabled}
                      onChange={(e) => setTranslationEnabled(e.target.checked)}
                  />
                  Enable Translation
              </label>
  
              {/* ✅ Language Selection (Only Visible When Enabled) */}
              {translationEnabled && (
                  <select
                      id="language-select"
                      className="language-dropdown"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                      <option value="">Select Language</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                  </select>
              )}
          </div>
  
          <button className="button" onClick={handleSubmit}>Update & Read</button>
      </div>
  );
  
};

export default TextInput;